import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDarkMode } from '../DarkModeContext';
import ExamPaperModal from './ExamPaperModal';
import PDFViewerModal from './PDFViewerModal';
import AnswerKeyModal from './AnswerKeyModal';
import { API_BASE_URL } from '../config';

function QuestionGeneration() {
  const location = useLocation();
  const navigate = useNavigate();
  const { dark } = useDarkMode();
  const { subject, chapterIndex, chapterName } = location.state || {};
  
  // Existing state
  const [examData, setExamData] = useState(null);
  const [showExamModal, setShowExamModal] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false); 
  const [pdfUrl, setPdfUrl] = useState(null); 
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false); 
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  // NEW STATE VARIABLES FOR TASK 1
  // Controls whether to show the two action buttons after successful generation
  const [showActionButtons, setShowActionButtons] = useState(false);
  
  // Controls the Answer Key modal (will be implemented in Task 3)
  const [showAnswerKeyModal, setShowAnswerKeyModal] = useState(false);
  
  // Stores the full API response for both question paper and answer key
  const [generatedData, setGeneratedData] = useState(null);

  // Effect to simulate progress when loading
  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) return prev; // Don't go to 100% until API call completes
          return prev + 10;
        });
      }, 500);
    } else {
      setProgress(0);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleView = async () => {
    setPdfLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/generate/view-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          subjectName: subject.toLowerCase(),
          chapterNumber: chapterIndex + 1
        })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        // Use presigned URL first, fallback to direct URL
        const urlToUse = result.presigned_url || result.direct_url;
        setPdfUrl(urlToUse);
        setShowPDFModal(true);
      } else {
        setError(result.detail || 'Failed to load PDF');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setPdfLoading(false);
    }
  };

  // UPDATED handleGeneratePaper function for new flow (REAL API)
  const handleGeneratePaper = async () => {
    setLoading(true);
    setError(null);
    // Hide action buttons while generating new paper
    setShowActionButtons(false);
    
    try {
      const response = await fetch(`${API_BASE_URL}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          subjectName: subject.toLowerCase(),
          chapterNumber: chapterIndex + 1
        })
      });
      
      const result = await response.json();
      console.log('API Response:', result);
      
      if (response.ok) {
        // Store the complete API response
        setGeneratedData(result);
        // Extract exam paper data - handle both possible API response formats
        setExamData(result.exam_paper || result.exam_paper_json || result);
        // Show the two action buttons instead of opening modal directly
        setShowActionButtons(true);
      } else {
        setError(result.detail || 'Failed to generate exam paper');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
      setProgress(100);
      setTimeout(() => setProgress(0), 500); // Smoothly hide the progress bar
    }
  };

  // NEW HANDLER FUNCTIONS FOR ACTION BUTTONS
  const handleQuestionPaper = () => {
    // Open the existing exam paper modal with questions only
    setShowExamModal(true);
  };

  const handleAnswerKey = () => {
    // Open the answer key modal (to be implemented in Task 3)
    setShowAnswerKeyModal(true);
  };

  // RESET FUNCTION to hide action buttons and clear data
  const resetGeneration = () => {
    setShowActionButtons(false);
    setGeneratedData(null);
    setExamData(null);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${dark ? 'bg-gray-900' : 'bg-gray-100'}`}>
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className={`
            flex items-center gap-2 mb-6 px-4 py-2 rounded-lg font-medium
            transition-colors duration-200
            ${dark 
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
              : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            }
          `}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Chapters
        </button>

        <h1 className={`text-3xl font-bold mb-2 ${dark ? 'text-blue-300' : 'text-blue-700'}`}>
          <span className="capitalize">{subject}</span>
          <span className="mx-2 text-gray-400">|</span>
          <span>Chapter {chapterIndex + 1}: {chapterName}</span>
        </h1>
        <p className={`mb-8 ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
          View Chapter Content or Generate Exam Paper
        </p>
        
        {/* Error Message */}
        {error && (
          <div className={`mb-6 p-4 rounded-lg ${dark ? 'bg-red-900/30 text-red-200' : 'bg-red-100 text-red-800'} flex justify-between items-center`}>
            <span>{error}</span>
            <button 
              onClick={() => setError(null)}
              className="text-xl font-bold"
            >
              &times;
            </button>
          </div>
        )}

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-2xl w-full max-w-md mx-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-1">Generating Exam Paper</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Please wait while we create your exam paper...</p>
              </div>
              
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden mb-2">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              
              <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                <span>0%</span>
                <span className="font-medium text-blue-600 dark:text-blue-400">{Math.min(progress, 100)}%</span>
                <span>100%</span>
              </div>
              
              <p className="text-xs text-center mt-3 text-gray-500 dark:text-gray-400">
                This may take a few moments. Please don't close this window.
              </p>
            </div>
          </div>
        )}

        {/* MAIN ACTION BUTTONS - Original View and Generate buttons */}
        {!showActionButtons && (
          <div className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4 mt-8">
            <button
              onClick={handleView}
              disabled={pdfLoading || loading}
              className={`
                flex-1 py-3 px-6 rounded-lg font-medium text-lg
                transition-all duration-200 transform hover:scale-105
                flex items-center justify-center gap-2
                ${(pdfLoading || loading) ? 'opacity-75 cursor-not-allowed' : ''}
                ${dark 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
                }
              `}
            >
              {pdfLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading PDF...
                </>
              ) : (
                'View Chapter Content'
              )}
            </button>
            
            <button
              onClick={handleGeneratePaper}
              disabled={loading || pdfLoading}
              className={`
                flex-1 py-3 px-6 rounded-lg font-medium text-lg
                transition-all duration-200 transform hover:scale-105
                flex items-center justify-center gap-2
                ${(loading || pdfLoading) ? 'opacity-75 cursor-not-allowed' : ''}
                ${dark 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-green-500 hover:bg-green-600 text-white'
                }
              `}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                'Generate Paper'
              )}
            </button>
          </div>
        )}

        {/* NEW POST-GENERATION ACTION BUTTONS */}
        {showActionButtons && generatedData && (
          <div className="mt-8 space-y-6">
            {/* Success Message */}
            <div className={`p-4 rounded-lg ${dark ? 'bg-green-900/30 text-green-200' : 'bg-green-100 text-green-800'} text-center`}>
              <h3 className="font-semibold mb-1">Exam Paper Generated Successfully!</h3>
              <p className="text-sm">Choose an option below to view or download the exam paper or answer key.</p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4">
              <button
                onClick={handleQuestionPaper}
                className={`
                  flex-1 py-3 px-6 rounded-lg font-medium text-lg
                  transition-all duration-200 transform hover:scale-105
                  flex items-center justify-center gap-2
                  ${dark 
                    ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                    : 'bg-purple-500 hover:bg-purple-600 text-white'
                  }
                `}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Question Paper
              </button>
              
              <button
                onClick={handleAnswerKey}
                className={`
                  flex-1 py-3 px-6 rounded-lg font-medium text-lg
                  transition-all duration-200 transform hover:scale-105
                  flex items-center justify-center gap-2
                  ${dark 
                    ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                    : 'bg-orange-500 hover:bg-orange-600 text-white'
                  }
                `}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                View Answer Key
              </button>
            </div>

            {/* Generate New Paper Button */}
            <div className="text-center">
              <button
                onClick={resetGeneration}
                className={`
                  px-4 py-2 rounded-lg font-medium text-sm
                  transition-colors duration-200
                  ${dark 
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                    : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }
                `}
              >
                Generate New Paper
              </button>
            </div>
          </div>
        )}

        {/* PDF Viewer Modal */}
        {showPDFModal && pdfUrl && (
          <PDFViewerModal 
            pdfUrl={pdfUrl}
            chapterTitle={`${subject} - Chapter ${chapterIndex + 1}`}
            onClose={() => setShowPDFModal(false)}
          />
        )}

        {/* Exam Paper Modal */}
        {showExamModal && (
          <ExamPaperModal 
            examData={examData}
            onClose={() => setShowExamModal(false)}
          />
        )}

        {/* Answer Key Modal */}
        {showAnswerKeyModal && (
          <AnswerKeyModal 
            generatedData={generatedData}
            onClose={() => setShowAnswerKeyModal(false)}
          />
        )}
      </div>
    </div>
  );
}

export default QuestionGeneration;